import React, { useState, useEffect } from 'react';
import { loadingImage } from '../auth';

const PATHS = ['PID', 'PR', 'MANR', 'SKID'];
const DWELL_BUCKETS = [
  { label: '0-1d', min: 0, max: 24 },
  { label: '1-2d', min: 24, max: 48 },
  { label: '2-3d', min: 48, max: 72 },
  { label: '3-4d', min: 72, max: 96 },
  { label: '4-5d', min: 96, max: 120 },
  { label: '5+d', min: 120, max: Infinity },
];

const PATH_COLORS = {
  PID: '#1565c0',
  PR: '#2e7d32',
  MANR: '#e65100',
  SKID: '#6a1b9a',
};

function getDwellBucket(hours) {
  for (const b of DWELL_BUCKETS) {
    if (hours >= b.min && hours < b.max) return b.label;
  }
  return '5+d';
}

export default function Fallin({ api, site }) {
  const [yardData, setYardData] = useState(null);
  const [shiftSnapshot, setShiftSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showDefects, setShowDefects] = useState(false);
  const [showTrailers, setShowTrailers] = useState(false);
  const [showDwell, setShowDwell] = useState(true);
  const [weeklyDwell, setWeeklyDwell] = useState(null);
  const [showWeekly, setShowWeekly] = useState(false);
  const [dwellFrom, setDwellFrom] = useState('');
  const [dwellTo, setDwellTo] = useState('');
  const [dwellShiftFilter, setDwellShiftFilter] = useState('all'); // all, FHD, FHN, BHD, BHN, WedD, WedN
  const [fmcFill, setFmcFill] = useState(null);
  const [fmcDate, setFmcDate] = useState('');
  const [fmcShift, setFmcShift] = useState('all');
  const [showFmc, setShowFmc] = useState(false);
  const [obFillData, setObFillData] = useState(null);
  const [obFillFrom, setObFillFrom] = useState('');
  const [obFillTo, setObFillTo] = useState('');
  const [obFillShift, setObFillShift] = useState('all');
  const [showObFill, setShowObFill] = useState(false);
  // OB Load Cube (Andes v_load_cube via LOAD_CUBE_<site> cache). Single combined
  // block: Loads + Avg Fill (fill = total_cube/equipment_cube), expandable
  // per-load table searchable by VRID.
  const [loadCube, setLoadCube] = useState(null);
  const [showLoadCube, setShowLoadCube] = useState(false);
  const [loadCubeVrid, setLoadCubeVrid] = useState('');
  const [activityMode, setActivityMode] = useState('shift'); // 'shift' = 0700-now, 'prior' = prior day
  const [dwellMode, setDwellMode] = useState('shift'); // same toggle for dwell report
  const [refreshInterval, setRefreshInterval] = useState(null);

  const fetchData = (skipDwell) => {
    setLoading(true);
    // Fetch live yard state
    fetch(`${api}/api/fclm/cache?site=${site}&date=YMS_YARD&shift_type=current`)
      .then(r => r.json())
      .then(data => {
        if (data?.trailers) {
          setYardData(data);
          setLastUpdated(data.fetched);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
    // Fetch shift snapshot (has dwell report)
    const now = new Date();
    const pacHour = parseInt(new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })).getHours());
    const pacDate = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    const dateStr = `${pacDate.getFullYear()}-${String(pacDate.getMonth()+1).padStart(2,'0')}-${String(pacDate.getDate()).padStart(2,'0')}`;
    const shift = (pacHour >= 8 && pacHour < 19) ? 'day' : 'night';
    fetch(`${api}/api/fclm/cache?site=${site}&date=YMS_SHIFT_${dateStr}&shift_type=${shift}`)
      .then(r => r.json())
      .then(data => { if (data?.completions) setShiftSnapshot(data); })
      .catch(() => {});
    // Fetch weekly dwell - use configurable date range (skip on 60s interval refresh)
    if (!skipDwell) {
    const fromDate = dwellFrom || (() => { const d = new Date(pacDate); d.setDate(d.getDate() - 6); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
    const toDate = dwellTo || `${pacDate.getFullYear()}-${String(pacDate.getMonth()+1).padStart(2,'0')}-${String(pacDate.getDate()).padStart(2,'0')}`;
    if (!dwellFrom) setDwellFrom(fromDate);
    if (!dwellTo) setDwellTo(toDate);
    const weekDates = [];
    const startD = new Date(fromDate + 'T12:00:00');
    const endD = new Date(toDate + 'T12:00:00');
    for (let d = new Date(startD); d <= endD; d.setDate(d.getDate() + 1)) {
      weekDates.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
    }
    Promise.all(weekDates.flatMap(d => ['day','night'].map(s =>
      fetch(`${api}/api/fclm/cache?site=${site}&date=YMS_SHIFT_${d}&shift_type=${s}`)
        .then(r => r.json()).then(data => ({ date: d, shift: s, completions: data?.completions || [], trailers: data?.trailers || [] }))
        .catch(() => ({ date: d, shift: s, completions: [], trailers: [] }))
    ))).then(results => {
      // Deduplicate by visitId - keep first (day before night)
      const seen = new Set();
      const allComps = [];
      results.sort((a, b) => a.date === b.date ? (a.shift === 'day' ? -1 : 1) : a.date.localeCompare(b.date));
      for (const r of results) {
        // Use trailers if available, otherwise use completions themselves for FIFO
        const hasTrailers = r.trailers && r.trailers.length > 0;
        const pathTrailers = {};
        if (hasTrailers) {
          r.trailers.forEach(t => {
            if (!t.path) return;
            if (!pathTrailers[t.path]) pathTrailers[t.path] = [];
            pathTrailers[t.path].push(t);
          });
        } else {
          // Use completions as proxy - all completions for this shift represent trailers that were on yard
          r.completions.forEach(c => {
            if (!c.path) return;
            if (!pathTrailers[c.path]) pathTrailers[c.path] = [];
            pathTrailers[c.path].push(c);
          });
        }
        Object.values(pathTrailers).forEach(arr => arr.sort((a,b) => (b.dwellHours||0) - (a.dwellHours||0)));
        
        for (const c of r.completions) {
          const dedupKey = c.visitId || c.isaId || `${c.vehicle}_${c.location}`;
          if (seen.has(dedupKey)) continue;
          seen.add(dedupKey);
          // Use pre-computed FIFO from yms-sync (per-shift logic)
          const wasOldest = c._fifoCompliant !== undefined ? c._fifoCompliant : true;
          const olderCount = c._trailersSkipped !== undefined ? c._trailersSkipped : 0;
          const excluded = c._excludedFromFifo || false;
          const bypassed = c._bypassed || [];
          // Live-load heuristic: a SKID trailer closed within 5h of arrival is a live
          // load — exclude it from FIFO (it's pulled to a door on arrival, not FIFO).
          const isLive = String(c.path || '').toUpperCase().startsWith('SK') && typeof c.dwellHours === 'number' && c.dwellHours <= 5;
          allComps.push({...c, _date: r.date, _shift: r.shift, _wasOldest: isLive ? true : wasOldest, _olderSkipped: olderCount, _excluded: isLive ? true : excluded, _bypassed: bypassed, _liveLoad: isLive});
        }
      }
      setWeeklyDwell(allComps);
    });
    } // end skipDwell guard
  };

  useEffect(() => {
    fetchData(false);
    const interval = setInterval(() => fetchData(true), 60000); // refresh yard only, not dwell
    return () => clearInterval(interval);
  }, [api, site]);

  // Re-fetch weekly dwell when date range changes
  useEffect(() => {
    if (dwellFrom && dwellTo) fetchData(false);
  }, [dwellFrom, dwellTo]);

  // Fetch FMC fill data
  useEffect(() => {
    if (!fmcDate) {
      const now = new Date();
      const y = now.getFullYear(), m = String(now.getMonth()+1).padStart(2,'0'), d = String(now.getDate()).padStart(2,'0');
      setFmcDate(`${y}-${m}-${d}`);
      return;
    }
    fetch(`${api}/api/fclm/cache?site=${site}&date=FMC_FILL_${fmcDate}&shift_type=all`)
      .then(r => r.json())
      .then(data => { if (data?.loads) setFmcFill(data); else setFmcFill(null); })
      .catch(() => setFmcFill(null));
  }, [api, site, fmcDate]);

  // Fetch OB Fill data from OB_FILL_{date} cache
  useEffect(() => {
    if (!obFillFrom || !obFillTo) {
      // Default: last 7 days
      const now = new Date();
      const from = new Date(now); from.setDate(now.getDate() - 6);
      const fromStr = `${from.getFullYear()}-${String(from.getMonth()+1).padStart(2,'0')}-${String(from.getDate()).padStart(2,'0')}`;
      const toStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
      if (!obFillFrom) setObFillFrom(fromStr);
      if (!obFillTo) setObFillTo(toStr);
      return;
    }
    const dates = [];
    const start = new Date(obFillFrom + 'T12:00:00');
    const end = new Date(obFillTo + 'T12:00:00');
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
    }
    Promise.all(dates.flatMap(d => ['day', 'night'].map(s =>
      fetch(`${api}/api/fclm/cache?site=${site}&date=OB_FILL_${d}&shift_type=${s}`)
        .then(r => r.json()).then(data => ({ date: d, shift: s, trailers: data?.trailers || [] }))
        .catch(() => ({ date: d, shift: s, trailers: [] }))
    ))).then(results => {
      const all = [];
      results.forEach(r => {
        r.trailers.forEach(t => all.push({ ...t, _date: r.date, _shift: r.shift }));
      });
      setObFillData(all);
    });
  }, [api, site, obFillFrom, obFillTo]);

  // Fetch OB Load Cube from LOAD_CUBE_<site> cache (one record per VRID).
  useEffect(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=LOAD_CUBE_${site}&shift_type=data`)
      .then(r => r.json())
      .then(data => { setLoadCube(data && Array.isArray(data.loads) ? data : null); })
      .catch(() => setLoadCube(null));
  }, [api, site]);

  // Build yard counts matrix
  const buildMatrix = (trailers, includeDefects) => {
    const matrix = {};
    PATHS.forEach(p => {
      matrix[p] = {};
      DWELL_BUCKETS.forEach(b => { matrix[p][b.label] = []; });
    });

    (trailers || []).forEach(t => {
      if (!includeDefects && t.isDefect) return;
      if (!t.path || !PATHS.includes(t.path)) return;
      if (!t.notes || !t.notes.toUpperCase().startsWith('IBVEND')) return;
      const bucket = getDwellBucket(t.dwellHours);
      matrix[t.path][bucket].push(t);
    });
    return matrix;
  };

  const matrix = yardData ? buildMatrix(yardData.trailers, showDefects) : null;
  const defects = (yardData?.trailers || []).filter(t => t.isDefect);

  // Path totals
  const pathTotals = (trailers) => {
    const totals = {};
    PATHS.forEach(p => totals[p] = (trailers || []).filter(t => t.path === p && (showDefects || !t.isDefect) && t.notes && t.notes.toUpperCase().startsWith('IBVEND')).length);
    return totals;
  };
  const totals = pathTotals(yardData?.trailers);

  // Link a trailer identifier to its YMS event history (by numeric visitId)
  const evtUrl = (visitId) => { const v = String(visitId || '').replace(/^V/, ''); if (!v) return null; const to = Date.now(); const from = to - 30 * 86400000; return `https://trans-logistics.amazon.com/yms/eventHistory#/eventReport?yard=${site}&fromDate=${from}&toDate=${to}&visitId=${v}`; };
  const idLink = (text, visitId) => { const u = evtUrl(visitId); return u ? <a href={u} target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0', textDecoration: 'underline' }}>{text}</a> : text; };

  const cellStyle = { padding: '6px 10px', textAlign: 'center', fontSize: '0.85rem', border: '1px solid #ddd', minWidth: 50 };
  const headerStyle = { ...cellStyle, fontWeight: 700, background: '#232f3e', color: '#fff' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 className="section-title" style={{ margin: 0 }}>FALLIN — {site}</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: '0.7rem', color: '#666' }}>
            {lastUpdated ? `Updated: ${new Date(lastUpdated).toLocaleTimeString()}` : 'No data'}
          </span>
          <button className="btn btn-secondary" onClick={fetchData} style={{ fontSize: '0.7rem', padding: '3px 10px' }}>
            🔄 Refresh
          </button>
          <label style={{ fontSize: '0.75rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={showDefects} onChange={e => setShowDefects(e.target.checked)} style={{ marginRight: 4 }} />
            Show Defects
          </label>
        </div>
      </div>

      {!yardData && !loading && (
        <div className="card">
          <p style={{ fontSize: '0.8rem', color: '#999' }}>
            No YMS data available. Run <code>bash /workspace/shiftplanner/backend/yms-sync.sh</code> from DevSpace to populate.
          </p>
        </div>
      )}

      {loading && !yardData && <div className="card" style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 40 }}><img src={loadingImage()} alt="Loading" style={{ width: 80, height: 80, animation: "spin 1.5s linear infinite" }} /><p style={{ marginTop: 12, fontSize: "0.85rem", color: "#666" }}>Loading yard data...</p><style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style></div>}

      {yardData && (
        <>
          {/* Summary bar */}
          <div className="card" style={{ padding: '8px 16px' }}>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ fontSize: '0.8rem' }}>
                <strong>Total IB:</strong> {(yardData.trailers || []).filter(t => (!showDefects ? !t.isDefect : true) && t.notes && t.notes.toUpperCase().startsWith('IBVEND')).length}
              </div>
              {PATHS.map(p => (
                <div key={p} style={{ fontSize: '0.8rem', color: PATH_COLORS[p], fontWeight: 600 }}>
                  {p}: {totals[p]}
                </div>
              ))}
              {defects.length > 0 && (
                <div style={{ fontSize: '0.8rem', color: '#ef5350', fontWeight: 600 }}>
                  ⚠️ Defects: {defects.length}
                </div>
              )}
              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                Completions today: {Object.values(yardData.completionsByPath || {}).reduce((s, v) => s + v, 0)}
                {' '}({PATHS.map(p => `${p}: ${yardData.completionsByPath?.[p] || 0}`).join(', ')})
              </div>
            </div>
          </div>

          {/* Yard Counts Table */}
          <div className="card" style={{ overflowX: 'auto' }}>
            <h3 className="section-title">Yard Counts</h3>
            <table style={{ borderCollapse: 'collapse', width: 'auto' }}>
              <thead>
                <tr>
                  <th style={{ ...headerStyle, textAlign: 'left', minWidth: 80 }}>Path</th>
                  {DWELL_BUCKETS.map(b => <th key={b.label} style={headerStyle}>{b.label}</th>)}
                  <th style={{ ...headerStyle, background: '#1a3a5c' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {PATHS.map(path => {
                  const row = matrix?.[path] || {};
                  const rowTotal = Object.values(row).reduce((s, arr) => s + arr.length, 0);
                  return (
                    <tr key={path}>
                      <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 700, color: PATH_COLORS[path], background: '#f9fafb' }}>
                        {path}
                      </td>
                      {DWELL_BUCKETS.map(b => {
                        const count = (row[b.label] || []).length;
                        const bg = b.label === '5+d' && count > 0 ? '#ffebee' :
                                   b.label === '4-5d' && count > 0 ? '#fff3e0' :
                                   b.label === '3-4d' && count > 0 ? '#fffde7' : '#fff';
                        return (
                          <td key={b.label} style={{ ...cellStyle, background: bg, fontWeight: count > 0 ? 600 : 400, color: b.label === '5+d' && count > 0 ? '#c62828' : '#333' }}>
                            {count || '—'}
                          </td>
                        );
                      })}
                      <td style={{ ...cellStyle, fontWeight: 700, background: '#e3f2fd', color: PATH_COLORS[path] }}>
                        {rowTotal}
                      </td>
                    </tr>
                  );
                })}
                {/* Total row */}
                <tr style={{ borderTop: '2px solid #000' }}>
                  <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 700, background: '#f9fafb' }}>TOTAL</td>
                  {DWELL_BUCKETS.map(b => {
                    const count = PATHS.reduce((s, p) => s + (matrix?.[p]?.[b.label]?.length || 0), 0);
                    return <td key={b.label} style={{ ...cellStyle, fontWeight: 700 }}>{count || '—'}</td>;
                  })}
                  <td style={{ ...cellStyle, fontWeight: 700, background: '#e3f2fd' }}>
                    {PATHS.reduce((s, p) => s + (totals[p] || 0), 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* On-Door Dwell - only trailers on production doors */}
          <div className="card" style={{ overflowX: 'auto' }}>
            <h3 className="section-title">On-Door Dwell (Production Doors)</h3>
            {(() => {
              const PROD_DOORS = {
                PID: ['DD352','DD353','DD354','DD355','DD356','DD357','DD358','DD359','DD360'],
                PR: ['DD143','DD146','DD150','DD153','DD156'],
                SKID: ['DD135','DD136','DD137','DD138'],
                MANR: ['DD347','DD348'],
              };
              const allProdDoors = Object.values(PROD_DOORS).flat();
              const onDoor = (yardData.trailers || []).filter(t => t.location && allProdDoors.includes(t.location.split('-')[0].split(' ')[0]) && !t.isDefect && t.path);
              const doorMatrix = {};
              PATHS.forEach(p => { doorMatrix[p] = {}; DWELL_BUCKETS.forEach(b => { doorMatrix[p][b.label] = []; }); });
              onDoor.forEach(t => {
                if (!PATHS.includes(t.path)) return;
                const bucket = getDwellBucket(t.dwellHours);
                doorMatrix[t.path][bucket].push(t);
              });
              return (
                <table style={{ borderCollapse: 'collapse', width: 'auto' }}>
                  <thead>
                    <tr>
                      <th style={{ ...cellStyle, textAlign: 'left', minWidth: 80, background: '#232f3e', color: '#fff', fontWeight: 700 }}>Path</th>
                      {DWELL_BUCKETS.map(b => <th key={b.label} style={headerStyle}>{b.label}</th>)}
                      <th style={{ ...headerStyle, background: '#1a3a5c' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PATHS.map(path => {
                      const row = doorMatrix[path];
                      const rowTotal = Object.values(row).reduce((s, arr) => s + arr.length, 0);
                      return (
                        <tr key={path}>
                          <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 700, color: PATH_COLORS[path], background: '#f9fafb' }}>{path}</td>
                          {DWELL_BUCKETS.map(b => {
                            const count = (row[b.label] || []).length;
                            const bg = b.label === '5+d' && count > 0 ? '#ffebee' : b.label === '4-5d' && count > 0 ? '#fff3e0' : '#fff';
                            return <td key={b.label} style={{ ...cellStyle, background: bg, fontWeight: count > 0 ? 600 : 400, color: b.label === '5+d' && count > 0 ? '#c62828' : '#333' }}>{count || '—'}</td>;
                          })}
                          <td style={{ ...cellStyle, fontWeight: 700, background: '#e3f2fd', color: PATH_COLORS[path] }}>{rowTotal}</td>
                        </tr>
                      );
                    })}
                    <tr style={{ borderTop: '2px solid #000' }}>
                      <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 700 }}>TOTAL</td>
                      {DWELL_BUCKETS.map(b => {
                        const count = PATHS.reduce((s, p) => s + (doorMatrix[p]?.[b.label]?.length || 0), 0);
                        return <td key={b.label} style={{ ...cellStyle, fontWeight: 700 }}>{count || '—'}</td>;
                      })}
                      <td style={{ ...cellStyle, fontWeight: 700, background: '#e3f2fd' }}>{onDoor.length}</td>
                    </tr>
                  </tbody>
                </table>
              );
            })()}
          </div>

          {/* Today's Activity */}
          {shiftSnapshot?.completions?.length > 0 && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h3 className="section-title" style={{ margin: 0 }}>Today's Activity</h3>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className={`btn ${activityMode === 'shift' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActivityMode('shift')} style={{ fontSize: '0.65rem', padding: '3px 8px' }}>0700–Now</button>
                  <button className={`btn ${activityMode === 'prior' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActivityMode('prior')} style={{ fontSize: '0.65rem', padding: '3px 8px' }}>Prior Day</button>
                </div>
              </div>
              {(() => {
                // Filter completions by time window
                const now = Date.now() / 1000;
                const pacNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
                // Today 7am Pacific in Unix seconds
                const todayPac = new Date(pacNow); todayPac.setHours(7, 0, 0, 0);
                const today7amUnix = todayPac.getTime() / 1000;
                const yesterday7amUnix = today7amUnix - 86400;
                
                let filtered;
                if (activityMode === 'shift') {
                  // 0700 today to now
                  filtered = shiftSnapshot.completions.filter(c => c.completionTime >= today7amUnix);
                } else {
                  // Prior day: yesterday 0700 to today 0700
                  filtered = shiftSnapshot.completions.filter(c => c.completionTime >= yesterday7amUnix && c.completionTime < today7amUnix);
                }
                
                // If no filter matches, show all (fallback)
                if (filtered.length === 0 && shiftSnapshot.completions.length > 0) {
                  filtered = shiftSnapshot.completions;
                }

                return (
                  <table style={{ borderCollapse: 'collapse', width: 'auto', fontSize: '0.8rem' }}>
                    <thead>
                      <tr>
                        <th style={headerStyle}></th>
                        {DWELL_BUCKETS.map(b => <th key={b.label} style={headerStyle}>{b.label}</th>)}
                        <th style={{ ...headerStyle, background: '#333' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {PATHS.map(path => {
                        const pathComps = filtered.filter(c => c.path === path);
                        if (pathComps.length === 0) return null;
                        return (
                          <tr key={path}>
                            <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 700, color: PATH_COLORS[path] }}>{path}</td>
                            {DWELL_BUCKETS.map(b => {
                              const count = pathComps.filter(c => c.dwellHours >= b.min && c.dwellHours < b.max).length;
                              const bg = b.label === '5+d' && count > 0 ? '#ffebee' : b.label === '4-5d' && count > 0 ? '#fff3e0' : '#fff';
                              return <td key={b.label} style={{ ...cellStyle, background: bg, fontWeight: count > 0 ? 600 : 400 }}>{count || '—'}</td>;
                            })}
                            <td style={{ ...cellStyle, fontWeight: 700, color: PATH_COLORS[path] }}>{pathComps.length}</td>
                          </tr>
                        );
                      })}
                      <tr style={{ borderTop: '2px solid #000' }}>
                        <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 700 }}>TOTAL</td>
                        {DWELL_BUCKETS.map(b => {
                          const count = filtered.filter(c => c.dwellHours >= b.min && c.dwellHours < b.max).length;
                          return <td key={b.label} style={{ ...cellStyle, fontWeight: 700 }}>{count || '—'}</td>;
                        })}
                        <td style={{ ...cellStyle, fontWeight: 700 }}>{filtered.length}</td>
                      </tr>
                      <tr>
                        <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 700 }}>Avg Dwell</td>
                        {DWELL_BUCKETS.map(b => <td key={b.label} style={cellStyle}></td>)}
                        {(() => { const all = filtered.filter(c => c.dwellHours > 0); const avg = all.length > 0 ? all.reduce((s,c) => s + c.dwellHours, 0) / all.length : 0; return <td style={{ ...cellStyle, fontWeight: 700 }}>{avg > 0 ? `${(avg/24).toFixed(1)}d` : '—'}</td>; })()}
                      </tr>
                    </tbody>
                  </table>
                );
              })()}
            </div>
          )}

          {/* Defects section */}
          {defects.length > 0 && (
            <div className="card">
              <h3 className="section-title" style={{ color: '#c62828' }}>⚠️ Defects / Unavailable ({defects.length})</h3>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.75rem' }}>
                <thead>
                  <tr>
                    <th style={headerStyle}>Location</th>
                    <th style={headerStyle}>Vehicle</th>
                    <th style={headerStyle}>Path</th>
                    <th style={headerStyle}>Dwell</th>
                    <th style={{ ...headerStyle, textAlign: 'left' }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {defects.map((t, i) => (
                    <tr key={i} style={{ background: '#fff3f3' }}>
                      <td style={cellStyle}>{t.location}</td>
                      <td style={cellStyle}>{idLink(t.vehicle, t.visitId)}</td>
                      <td style={{ ...cellStyle, color: PATH_COLORS[t.path] || '#333', fontWeight: 600 }}>{t.path || '?'}</td>
                      <td style={cellStyle}>{t.dwellHours > 24 ? `${(t.dwellHours/24).toFixed(1)}d` : `${Math.round(t.dwellHours)}h`}</td>
                      <td style={{ ...cellStyle, textAlign: 'left', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Dwell Report */}
          {shiftSnapshot?.completions?.length > 0 && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="section-title" style={{ cursor: 'pointer', margin: 0 }} onClick={() => setShowDwell(!showDwell)}>
                  📊 Dwell Report <span style={{ fontSize: '0.7rem', color: '#666' }}>{showDwell ? '▼' : '▶'}</span>
                </h3>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className={`btn ${dwellMode === 'shift' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setDwellMode('shift')} style={{ fontSize: '0.65rem', padding: '3px 8px' }}>0700–Now</button>
                  <button className={`btn ${dwellMode === 'prior' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setDwellMode('prior')} style={{ fontSize: '0.65rem', padding: '3px 8px' }}>Prior Day</button>
                </div>
              </div>
              {showDwell && (() => {
                const pacNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
                const todayPac = new Date(pacNow); todayPac.setHours(7, 0, 0, 0);
                const today7amUnix = todayPac.getTime() / 1000;
                const yesterday7amUnix = today7amUnix - 86400;
                
                let filtered;
                if (dwellMode === 'shift') {
                  filtered = shiftSnapshot.completions.filter(c => c.completionTime >= today7amUnix);
                } else {
                  filtered = shiftSnapshot.completions.filter(c => c.completionTime >= yesterday7amUnix && c.completionTime < today7amUnix);
                }
                if (filtered.length === 0) filtered = shiftSnapshot.completions;

                const formatTime = (ts) => {
                  if (!ts) return '—';
                  const d = new Date(ts * 1000);
                  return d.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
                };

                return (
                  <>
                    <div style={{ display: 'flex', gap: 16, margin: '8px 0', fontSize: '0.8rem' }}>
                      {PATHS.map(p => {
                        const pathComps = filtered.filter(c => c.path === p);
                        const avgDwell = pathComps.length > 0 ? pathComps.reduce((s, c) => s + c.dwellHours, 0) / pathComps.length : 0;
                        return pathComps.length > 0 ? (
                          <div key={p} style={{ color: PATH_COLORS[p], fontWeight: 600 }}>
                            {p}: {pathComps.length} (avg {avgDwell > 24 ? `${(avgDwell/24).toFixed(1)}d` : `${Math.round(avgDwell)}h`})
                          </div>
                        ) : null;
                      })}
                      <div style={{ fontWeight: 700 }}>Total: {filtered.length}</div>
                    </div>
                    {PATHS.map(path => {
                      const pathComps = filtered.filter(c => c.path === path);
                      if (pathComps.length === 0) return null;
                      const avgDwell = pathComps.reduce((s, c) => s + c.dwellHours, 0) / pathComps.length;
                      return (
                        <div key={path} style={{ marginBottom: 16 }}>
                          <h4 style={{ margin: '8px 0 4px', color: PATH_COLORS[path], fontSize: '0.8rem', fontWeight: 700 }}>
                            {path} — {pathComps.length} completions (avg: {avgDwell > 24 ? `${(avgDwell/24).toFixed(1)}d` : `${Math.round(avgDwell)}h`})
                          </h4>
                          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.7rem' }}>
                            <thead>
                              <tr>
                                <th style={{ ...cellStyle, background: PATH_COLORS[path], color: '#fff', fontWeight: 700 }}>Location</th>
                                <th style={{ ...cellStyle, background: PATH_COLORS[path], color: '#fff', fontWeight: 700 }}>Vehicle</th>
                                <th style={{ ...cellStyle, background: PATH_COLORS[path], color: '#fff', fontWeight: 700 }}>Dwell</th>
                                <th style={{ ...cellStyle, background: PATH_COLORS[path], color: '#fff', fontWeight: 700 }}>Arrived</th>
                                <th style={{ ...cellStyle, background: PATH_COLORS[path], color: '#fff', fontWeight: 700 }}>Completed</th>
                                <th style={{ ...cellStyle, background: PATH_COLORS[path], color: '#fff', fontWeight: 700 }}>ISA</th>
                                <th style={{ ...cellStyle, background: PATH_COLORS[path], color: '#fff', fontWeight: 700, textAlign: 'left' }}>Notes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pathComps
                                .sort((a, b) => (b.dwellHours || 0) - (a.dwellHours || 0))
                                .map((c, i) => {
                                  const dwellDays = c.dwellHours / 24;
                                  const bg = dwellDays >= 5 ? '#ffcdd2' : dwellDays >= 3 ? '#fff3e0' : i % 2 === 0 ? '#fff' : '#f9fafb';
                                  return (
                                    <tr key={i} style={{ background: bg }}>
                                      <td style={cellStyle}>{c.location}</td>
                                      <td style={cellStyle}>{idLink(c.vehicle, c.visitId)}</td>
                                      <td style={{ ...cellStyle, fontWeight: 600, color: dwellDays >= 5 ? '#c62828' : dwellDays >= 3 ? '#e65100' : '#333' }}>
                                        {c.dwellHours > 0 ? (dwellDays >= 1 ? `${dwellDays.toFixed(1)}d` : `${Math.round(c.dwellHours)}h`) : '—'}
                                      </td>
                                      <td style={{ ...cellStyle, fontSize: '0.6rem' }}>{formatTime(c.checkInTime)}</td>
                                      <td style={{ ...cellStyle, fontSize: '0.6rem' }}>{formatTime(c.completionTime)}</td>
                                      <td style={cellStyle}>{c.isaId || '—'}</td>
                                      <td style={{ ...cellStyle, textAlign: 'left', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.notes}</td>
                                    </tr>
                                  );
                              })}
                            </tbody>
                        </table>
                      </div>
                    );
                  })}
                  </>
                );
              })()}
            </div>
          )}

          {/* EOS Chat */}
          {yardData?.eos && (
            <div className="card">
              <h3 className="section-title">EOS Chat</h3>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                {/* Yard Utilization */}
                <div>
                  <h4 style={{ fontSize: '0.8rem', margin: '0 0 8px', fontWeight: 700 }}>Yard Utilization</h4>
                  <table style={{ borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                    <tbody>
                      <tr><td style={{ ...cellStyle, textAlign: 'left', fontWeight: 600 }}>Onsite</td><td style={cellStyle}>{yardData.eos.onsite}</td><td style={{ ...cellStyle, color: '#666' }}>/ 356</td><td style={cellStyle}>{Math.round(yardData.eos.onsite / 356 * 100)}%</td></tr>
                      <tr><td style={{ ...cellStyle, textAlign: 'left', fontWeight: 600 }}>Offsite (OSY)</td><td style={cellStyle}>{yardData.eos.offsite}</td><td style={{ ...cellStyle, color: '#666' }}>/ 178</td><td style={cellStyle}>{Math.round(yardData.eos.offsite / 178 * 100)}%</td></tr>
                      <tr style={{ borderTop: '1px solid #ccc' }}>
                        <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 700 }}></td>
                        <td style={{ ...cellStyle, fontWeight: 700 }}>Onsite</td>
                        <td style={{ ...cellStyle, fontWeight: 700 }}>Offsite</td>
                        <td style={{ ...cellStyle, fontWeight: 700 }}>Total</td>
                      </tr>
                      <tr><td style={{ ...cellStyle, textAlign: 'left' }}>IB Full</td><td style={cellStyle}>{yardData.eos.ibFullOnsite || 0}</td><td style={cellStyle}>{yardData.eos.ibFullOffsite || 0}</td><td style={{ ...cellStyle, fontWeight: 600 }}>{yardData.eos.ibFull}</td></tr>
                      <tr><td style={{ ...cellStyle, textAlign: 'left' }}>IB Empty</td><td style={cellStyle}>{yardData.eos.ibEmptyOnsite || 0}</td><td style={cellStyle}>{yardData.eos.ibEmptyOffsite || 0}</td><td style={{ ...cellStyle, fontWeight: 600 }}>{yardData.eos.ibEmpty}</td></tr>
                      <tr><td style={{ ...cellStyle, textAlign: 'left' }}>OB Full</td><td style={cellStyle}>{yardData.eos.obFullOnsite || 0}</td><td style={cellStyle}>{yardData.eos.obFullOffsite || 0}</td><td style={{ ...cellStyle, fontWeight: 600 }}>{yardData.eos.obFull}</td></tr>
                      <tr><td style={{ ...cellStyle, textAlign: 'left' }}>OB Empty</td><td style={cellStyle}>{yardData.eos.obEmptyOnsite || 0}</td><td style={cellStyle}>{yardData.eos.obEmptyOffsite || 0}</td><td style={{ ...cellStyle, fontWeight: 600 }}>{yardData.eos.obEmpty}</td></tr>
                      <tr><td style={{ ...cellStyle, textAlign: 'left' }}>Other</td><td style={cellStyle}>{yardData.eos.otherOnsite || 0}</td><td style={cellStyle}>{yardData.eos.otherOffsite || 0}</td><td style={{ ...cellStyle, fontWeight: 600 }}>{yardData.eos.other}</td></tr>
                    </tbody>
                  </table>
                </div>
                {/* RTDs */}
                <div>
                  <h4 style={{ fontSize: '0.8rem', margin: '0 0 8px', fontWeight: 700 }}>RTDs (Ready to Depart)</h4>
                  <table style={{ borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                    <tbody>
                      <tr><td style={{ ...cellStyle, textAlign: 'left', fontWeight: 600 }}>Total RTDs</td><td style={cellStyle}>{(yardData.eos.rtd || []).length}</td></tr>
                      <tr><td style={{ ...cellStyle, textAlign: 'left', color: '#e65100' }}>&gt;12hr at location</td><td style={{ ...cellStyle, color: '#e65100', fontWeight: 600 }}>{yardData.eos.rtd12}</td></tr>
                      <tr><td style={{ ...cellStyle, textAlign: 'left', color: '#c62828' }}>&gt;72hr at location</td><td style={{ ...cellStyle, color: '#c62828', fontWeight: 600 }}>{yardData.eos.rtd72}</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Weekly Dwell */}
          {weeklyDwell && weeklyDwell.length > 0 && (
            <div className="card">
              <h3 className="section-title" style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} onClick={() => setShowWeekly(!showWeekly)}>
                <span>📅 Dwell Report ({weeklyDwell.length} completions{dwellShiftFilter !== 'all' ? ` — filtered` : ''})</span>
                <span style={{ fontSize: '0.7rem', color: '#666' }}>{showWeekly ? '▼ Hide' : '▶ Show'}</span>
              </h3>
              {showWeekly && (<>
                <div style={{ display: 'flex', gap: 8, margin: '4px 0 8px', alignItems: 'center', fontSize: '0.7rem' }}>
                  <label>From: <input type="date" value={dwellFrom} onChange={e => setDwellFrom(e.target.value)} style={{ fontSize: '0.7rem' }} /></label>
                  <label>To: <input type="date" value={dwellTo} onChange={e => setDwellTo(e.target.value)} style={{ fontSize: '0.7rem' }} /></label>
                  <button onClick={() => { const d = new Date(); d.setDate(d.getDate() - (d.getDay() || 7)); const sun = new Date(d); sun.setDate(d.getDate() - d.getDay()); const sat = new Date(sun); sat.setDate(sun.getDate() + 6); setDwellFrom(sun.toISOString().split('T')[0]); setDwellTo(sat.toISOString().split('T')[0]); }} style={{ fontSize: '0.65rem', padding: '2px 6px', cursor: 'pointer' }}>Last Week</button>
                  <button onClick={() => { const d = new Date(); d.setDate(d.getDate() - 6); setDwellFrom(d.toISOString().split('T')[0]); setDwellTo(new Date().toISOString().split('T')[0]); }} style={{ fontSize: '0.65rem', padding: '2px 6px', cursor: 'pointer' }}>Last 7 Days</button>
                  <button onClick={() => { const d = new Date(); d.setDate(d.getDate() - 29); setDwellFrom(d.toISOString().split('T')[0]); setDwellTo(new Date().toISOString().split('T')[0]); }} style={{ fontSize: '0.65rem', padding: '2px 6px', cursor: 'pointer' }}>Last 30 Days</button>
                  <button onClick={() => { setDwellFrom('2026-07-01'); setDwellTo('2026-07-31'); }} style={{ fontSize: '0.65rem', padding: '2px 6px', cursor: 'pointer' }}>July</button>
                </div>
                <div style={{ display: 'flex', gap: 4, margin: '0 0 8px', alignItems: 'center', fontSize: '0.65rem' }}>
                  <strong style={{ marginRight: 4 }}>Shift:</strong>
                  {[['all','All'],['FHD','FH Day'],['FHN','FH Night'],['BHD','BH Day'],['BHN','BH Night'],['WedD','Wed Day'],['WedN','Wed Night']].map(([val,label]) => (
                    <button key={val} className={`btn ${dwellShiftFilter === val ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setDwellShiftFilter(val)} style={{ fontSize: '0.6rem', padding: '2px 6px' }}>{label}</button>
                  ))}
                </div>
              {(() => {
                // Apply shift filter
                const FH_DAYS = [0, 1, 2]; // Sun=0, Mon=1, Tue=2
                const BH_DAYS = [4, 5, 6]; // Thu=4, Fri=5, Sat=6
                const WED_DAY = 3;
                const filterComp = (c) => {
                  if (dwellShiftFilter === 'all') return true;
                  const dayOfWeek = new Date(c._date + 'T12:00:00').getDay();
                  const shift = c._shift;
                  switch (dwellShiftFilter) {
                    case 'FHD': return FH_DAYS.includes(dayOfWeek) && shift === 'day';
                    case 'FHN': return FH_DAYS.includes(dayOfWeek) && shift === 'night';
                    case 'BHD': return BH_DAYS.includes(dayOfWeek) && shift === 'day';
                    case 'BHN': return BH_DAYS.includes(dayOfWeek) && shift === 'night';
                    case 'WedD': return dayOfWeek === WED_DAY && shift === 'day';
                    case 'WedN': return dayOfWeek === WED_DAY && shift === 'night';
                    default: return true;
                  }
                };
                const filteredDwell = weeklyDwell.filter(filterComp);
                const withDwell = filteredDwell;
                const allPaths = [...PATHS, 'UNKNOWN'];
                const formatTime = (ts) => { if (!ts) return '—'; return new Date(ts * 1000).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }); };
                return (
                  <>
                    {/* Summary */}
                    <div style={{ display: 'flex', gap: 16, margin: '8px 0', fontSize: '0.8rem', flexWrap: 'wrap' }}>
                      {allPaths.map(p => {
                        const pc = filteredDwell.filter(c => (c.path || 'UNKNOWN') === p);
                        const pcDwell = pc.filter(c => c.dwellHours > 0);
                        const avg = pcDwell.length > 0 ? pcDwell.reduce((s,c) => s + c.dwellHours, 0) / pcDwell.length : 0;
                        return pc.length > 0 ? <div key={p} style={{ color: PATH_COLORS[p] || '#666', fontWeight: 600 }}>{p}: {pc.length}{pcDwell.length < pc.length ? ` (${pcDwell.length} w/dwell)` : ''} {avg > 0 ? `(avg ${(avg/24).toFixed(1)}d)` : ''}</div> : null;
                      })}
                      <div style={{ fontWeight: 700 }}>Total: {filteredDwell.length} ({withDwell.length} with dwell, avg {withDwell.length > 0 ? (withDwell.reduce((s,c)=>s+c.dwellHours,0)/withDwell.length/24).toFixed(1) : 0}d)</div>
                    </div>
                    {/* Dwell bucket matrix */}
                    <table style={{ borderCollapse: 'collapse', width: 'auto', fontSize: '0.8rem', marginBottom: 12 }}>
                      <thead>
                        <tr>
                          <th style={{ ...cellStyle, textAlign: 'left', background: '#232f3e', color: '#fff', fontWeight: 700 }}>Path</th>
                          {DWELL_BUCKETS.map(b => <th key={b.label} style={headerStyle}>{b.label}</th>)}
                          <th style={{ ...headerStyle, background: '#333' }}>Total</th>
                          <th style={headerStyle}>Avg</th>
                          <th style={headerStyle}>FIFO %</th>
                          <th style={{ ...headerStyle, background: '#5c6bc0', fontSize: '0.55rem' }}>Recat</th>
                          <th style={{ ...headerStyle, background: '#5c6bc0', fontSize: '0.55rem' }}>→PID</th>
                          <th style={{ ...headerStyle, background: '#5c6bc0', fontSize: '0.55rem' }}>PID→</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allPaths.map(path => {
                          const pc = withDwell.filter(c => c.path === path);
                          if (pc.length === 0) return null;
                          const avg = pc.reduce((s,c) => s + c.dwellHours, 0) / pc.length;
                          const fifoEligible = pc.filter(c => !c._excluded);
                          const fifoCount = fifoEligible.filter(c => c._wasOldest).length;
                          const fifoPct = fifoEligible.length > 0 ? Math.round(fifoCount / fifoEligible.length * 100) : 0;
                          return (
                            <tr key={path}>
                              <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 700, color: PATH_COLORS[path] }}>{path}</td>
                              {DWELL_BUCKETS.map(b => {
                                const count = pc.filter(c => c.dwellHours >= b.min && c.dwellHours < b.max).length;
                                return <td key={b.label} style={{ ...cellStyle, fontWeight: count > 0 ? 600 : 400, background: b.label === '5+d' && count > 0 ? '#ffebee' : '#fff' }}>{count || '—'}</td>;
                              })}
                              <td style={{ ...cellStyle, fontWeight: 700 }}>{pc.length}</td>
                              <td style={cellStyle}>{(avg/24).toFixed(1)}d</td>
                              <td style={{ ...cellStyle, fontWeight: 600, color: fifoPct >= 90 ? '#2e7d32' : fifoPct >= 75 ? '#f57f17' : '#c62828' }}>{fifoPct}%</td>
                              {/* Recat: completions for this path that were recategorized */}
                              <td style={{ ...cellStyle, color: '#5c6bc0', fontWeight: 500 }}>{pc.filter(c => c._recategorized && c._recategorized !== '').length || '—'}</td>
                              {/* →PID: trailers noted as THIS path but completed on PID */}
                              <td style={{ ...cellStyle, color: '#5c6bc0', fontWeight: 500 }}>{path !== 'PID' ? (withDwell.filter(c => c.path === 'PID' && (c._recategorized === `Was ${path}` || (c.notes === path))).length || '—') : (withDwell.filter(c => c.path === 'PID' && c._recategorized && c._recategorized !== '').length || '—')}</td>
                              {/* PID→: trailers noted as PID but completed on THIS path */}
                              <td style={{ ...cellStyle, color: '#5c6bc0', fontWeight: 500 }}>{path !== 'PID' ? (pc.filter(c => c._recategorized === 'Was PID' || c.notes === 'PID').length || '—') : (withDwell.filter(c => c.path !== 'PID' && (c._recategorized === 'Was PID' || c.notes === 'PID')).length || '—')}</td>
                            </tr>
                          );
                        })}
                        <tr style={{ borderTop: '2px solid #000' }}>
                          <td style={{ ...cellStyle, fontWeight: 700 }}>TOTAL</td>
                          {DWELL_BUCKETS.map(b => <td key={b.label} style={{ ...cellStyle, fontWeight: 700 }}>{withDwell.filter(c => c.dwellHours >= b.min && c.dwellHours < b.max).length || '—'}</td>)}
                          <td style={{ ...cellStyle, fontWeight: 700 }}>{withDwell.length}</td>
                          <td style={{ ...cellStyle, fontWeight: 700 }}>{withDwell.length > 0 ? (withDwell.reduce((s,c)=>s+c.dwellHours,0)/withDwell.length/24).toFixed(1) : '—'}d</td>
                          {(() => { const eligible = withDwell.filter(c => !c._excluded); const f = eligible.filter(c => c._wasOldest).length; const pct = eligible.length > 0 ? Math.round(f / eligible.length * 100) : 0; return <td style={{ ...cellStyle, fontWeight: 700, color: pct >= 90 ? '#2e7d32' : pct >= 75 ? '#f57f17' : '#c62828' }}>{pct}%</td>; })()}
                          <td style={{ ...cellStyle, fontWeight: 700, color: '#5c6bc0' }}>{withDwell.filter(c => c._recategorized && c._recategorized !== '').length || '—'}</td>
                          <td style={{ ...cellStyle, fontWeight: 700, color: '#5c6bc0' }}>{withDwell.filter(c => c.path === 'PID' && c._recategorized && c._recategorized !== '').length || '—'}</td>
                          <td style={{ ...cellStyle, fontWeight: 700, color: '#5c6bc0' }}>{withDwell.filter(c => c.path !== 'PID' && (c._recategorized === 'Was PID' || c.notes === 'PID')).length || '—'}</td>
                        </tr>
                      </tbody>
                    </table>
                    {/* Detail by path */}
                    {allPaths.map(path => {
                      const pc = withDwell.filter(c => c.path === path).sort((a,b) => b.dwellHours - a.dwellHours);
                      if (pc.length === 0) return null;
                      return (
                        <details key={path} style={{ marginBottom: 8 }}>
                          <summary style={{ cursor: 'pointer', fontWeight: 700, color: PATH_COLORS[path], fontSize: '0.8rem' }}>{path} — {pc.length} completions ({pc.filter(c => !c._wasOldest).length} FIFO violations)</summary>
                          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.65rem', marginTop: 4 }}>
                            <thead><tr>
                              <th style={{ ...cellStyle, background: PATH_COLORS[path], color: '#fff', fontSize: '0.6rem' }}>Date</th>
                              <th style={{ ...cellStyle, background: PATH_COLORS[path], color: '#fff', fontSize: '0.6rem' }}>Shift</th>
                              <th style={{ ...cellStyle, background: PATH_COLORS[path], color: '#fff', fontSize: '0.6rem' }}>ISA</th>
                              <th style={{ ...cellStyle, background: PATH_COLORS[path], color: '#fff', fontSize: '0.6rem' }}>DD</th>
                              <th style={{ ...cellStyle, background: PATH_COLORS[path], color: '#fff', fontSize: '0.6rem' }}>Dwell</th>
                              <th style={{ ...cellStyle, background: PATH_COLORS[path], color: '#fff', fontSize: '0.6rem' }}>Plate</th>
                              <th style={{ ...cellStyle, background: PATH_COLORS[path], color: '#fff', fontSize: '0.6rem' }}>Notes</th>
                              <th style={{ ...cellStyle, background: PATH_COLORS[path], color: '#fff', fontSize: '0.6rem' }}>FIFO</th>
                            </tr></thead>
                            <tbody>
                              {pc.map((c,i) => (
                                <tr key={i} style={{ background: c._liveLoad ? '#f3e5f5' : !c._wasOldest ? '#ffcdd2' : c.dwellHours/24 >= 5 ? '#ffebee' : c.dwellHours/24 >= 3 ? '#fff3e0' : i%2===0 ? '#fff' : '#f9fafb' }}>
                                  <td style={cellStyle}>{c._date}</td>
                                  <td style={cellStyle}>{c._shift}</td>
                                  <td style={cellStyle}>{c.isaId || '—'}</td>
                                  <td style={cellStyle}>{c.location}</td>
                                  <td style={{ ...cellStyle, fontWeight: 600 }}>{(c.dwellHours/24).toFixed(1)}d</td>
                                  <td style={cellStyle}>{idLink(c.vehicle || '—', c.visitId)}</td>
                                  <td style={{ ...cellStyle, maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={c.notes}>{c._liveLoad ? 'Live Load' : (c.notes || '—')}</td>
                                  <td style={{ ...cellStyle, fontWeight: 600, color: c._liveLoad ? '#6a1b9a' : c._wasOldest ? '#4caf50' : '#c62828', maxWidth: 250 }}>
                                    {c._liveLoad ? '🔴 Live' : c._wasOldest ? '✓' : <>
                                      ✗ ({c._olderSkipped} older)
                                      {c._olderSkipped <= 5 && c._bypassed && c._bypassed.length > 0 && (() => {
                                        // Dedup; show the plate when available, else the trailer/visit ID
                                        const seen = new Set();
                                        const shown = c._bypassed.filter(b => {
                                          const id = (b.id || '').replace(/^V/, '');
                                          if (!id || seen.has(id)) return false;
                                          seen.add(id); return true;
                                        });
                                        return shown.length > 0 ? (
                                          <div style={{ fontSize: '0.55rem', color: '#666', fontWeight: 400, marginTop: 2 }}>
                                            {shown.map((b, bi) => {
                                              const txt = (b.id || '').replace(/^V/, '');
                                              const vid = b.visitId ? String(b.visitId).replace(/^V/, '') : (/^\d+$/.test(txt) ? txt : '');
                                              const label = `${txt} (${b.dwell}d @ ${b.location || '?'})`;
                                              if (!vid) return <div key={bi}>{label}</div>;
                                              const to = Date.now(); const from = to - 30 * 86400000;
                                              const url = `https://trans-logistics.amazon.com/yms/eventHistory#/eventReport?yard=${site}&fromDate=${from}&toDate=${to}&visitId=${vid}`;
                                              return <div key={bi}><a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0', textDecoration: 'underline' }}>{label}</a></div>;
                                            })}
                                          </div>
                                        ) : null;
                                      })()}
                                    </>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </details>
                      );
                    })}
                  </>
                );
              })()}
              </>)}
            </div>
          )}

          {/* OB Trailer Fill (Historical - from OB_FILL cache) */}
          <div className="card">
            <h3 className="section-title" style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} onClick={() => setShowObFill(!showObFill)}>
              <span>📦 OB Trailer Fill</span>
              <span style={{ fontSize: '0.7rem', color: '#666' }}>{showObFill ? '▼ Hide' : '▶ Show'}</span>
            </h3>
            {showObFill && (<>
              <div style={{ display: 'flex', gap: 8, margin: '4px 0 8px', alignItems: 'center', fontSize: '0.7rem', flexWrap: 'wrap' }}>
                <label>From: <input type="date" value={obFillFrom} onChange={e => setObFillFrom(e.target.value)} style={{ fontSize: '0.7rem' }} /></label>
                <label>To: <input type="date" value={obFillTo} onChange={e => setObFillTo(e.target.value)} style={{ fontSize: '0.7rem' }} /></label>
                <button onClick={() => { const d = new Date(); d.setDate(d.getDate() - 6); setObFillFrom(d.toISOString().split('T')[0]); setObFillTo(new Date().toISOString().split('T')[0]); }} style={{ fontSize: '0.65rem', padding: '2px 6px', cursor: 'pointer' }}>Last 7 Days</button>
                <button onClick={() => { const d = new Date(); d.setDate(d.getDate() - 29); setObFillFrom(d.toISOString().split('T')[0]); setObFillTo(new Date().toISOString().split('T')[0]); }} style={{ fontSize: '0.65rem', padding: '2px 6px', cursor: 'pointer' }}>Last 30 Days</button>
                <button onClick={() => { setObFillFrom('2026-07-01'); setObFillTo('2026-07-31'); }} style={{ fontSize: '0.65rem', padding: '2px 6px', cursor: 'pointer' }}>July</button>
              </div>
              <div style={{ display: 'flex', gap: 4, margin: '0 0 8px', alignItems: 'center', fontSize: '0.65rem' }}>
                <strong style={{ marginRight: 4 }}>Shift:</strong>
                {[['all','All'],['FHD','FH Day'],['FHN','FH Night'],['BHD','BH Day'],['BHN','BH Night'],['WedD','Wed Day'],['WedN','Wed Night']].map(([val,label]) => (
                  <button key={val} className={`btn ${obFillShift === val ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setObFillShift(val)} style={{ fontSize: '0.6rem', padding: '2px 6px' }}>{label}</button>
                ))}
              </div>
              {obFillData && (() => {
                const FH_DAYS = [0, 1, 2]; const BH_DAYS = [4, 5, 6]; const WED_DAY = 3;
                const filtered = obFillData.filter(t => {
                  if (obFillShift === 'all') return true;
                  const dow = new Date(t._date + 'T12:00:00').getDay();
                  const s = t._shift;
                  switch (obFillShift) {
                    case 'FHD': return FH_DAYS.includes(dow) && s === 'day';
                    case 'FHN': return FH_DAYS.includes(dow) && s === 'night';
                    case 'BHD': return BH_DAYS.includes(dow) && s === 'day';
                    case 'BHN': return BH_DAYS.includes(dow) && s === 'night';
                    case 'WedD': return dow === WED_DAY && s === 'day';
                    case 'WedN': return dow === WED_DAY && s === 'night';
                    default: return true;
                  }
                });
                const CATS = ['Fluid Direct', 'Fluid Offline', 'Palletized'];
                const thS = { padding: '4px 8px', border: '1px solid #ddd', fontSize: '0.7rem', background: '#333', color: '#fff', textAlign: 'center' };
                const tdS = { padding: '4px 8px', border: '1px solid #ddd', fontSize: '0.75rem', textAlign: 'center' };
                return (<>
                  <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 8 }}>
                    <thead><tr>
                      <th style={{ ...thS, textAlign: 'left' }}>Category</th>
                      <th style={thS}>Loads</th>
                      <th style={thS}>Avg Fill %</th>
                      <th style={thS}>Avg Cubic Vol</th>
                      <th style={thS}>Avg Cartons</th>
                    </tr></thead>
                    <tbody>
                      {CATS.map(cat => {
                        const cl = filtered.filter(t => t.category === cat);
                        if (cl.length === 0) return null;
                        const avgFill = (cl.reduce((s,t) => s + (t.fillPct||0), 0) / cl.length).toFixed(1);
                        const avgVol = Math.round(cl.reduce((s,t) => s + (t.cubicVolume||0), 0) / cl.length);
                        const avgCart = Math.round(cl.reduce((s,t) => s + (t.cartons||0), 0) / cl.length);
                        const catColor = cat === 'Fluid Direct' ? '#1565c0' : cat === 'Palletized' ? '#e65100' : '#6a1b9a';
                        return (
                          <tr key={cat}>
                            <td style={{ ...tdS, textAlign: 'left', fontWeight: 600, color: catColor }}>{cat}</td>
                            <td style={tdS}>{cl.length}</td>
                            <td style={{ ...tdS, fontWeight: 600, color: parseFloat(avgFill) >= 65 ? '#2e7d32' : parseFloat(avgFill) >= 55 ? '#f57f17' : '#c62828' }}>{avgFill}%</td>
                            <td style={tdS}>{avgVol.toLocaleString()}</td>
                            <td style={tdS}>{avgCart.toLocaleString()}</td>
                          </tr>
                        );
                      })}
                      {/* Fluid Total (Direct + Offline combined) */}
                      {(() => {
                        const fl = filtered.filter(t => t.category === 'Fluid Direct' || t.category === 'Fluid Offline');
                        if (fl.length === 0) return null;
                        return (
                          <tr style={{ borderTop: '1px solid #999', background: '#e3f2fd', fontWeight: 600 }}>
                            <td style={{ ...tdS, textAlign: 'left', color: '#1565c0' }}>Fluid Total</td>
                            <td style={tdS}>{fl.length}</td>
                            <td style={{ ...tdS, fontWeight: 600, color: (fl.reduce((s,t)=>s+(t.fillPct||0),0)/fl.length) >= 65 ? '#2e7d32' : (fl.reduce((s,t)=>s+(t.fillPct||0),0)/fl.length) >= 55 ? '#f57f17' : '#c62828' }}>{(fl.reduce((s,t)=>s+(t.fillPct||0),0)/fl.length).toFixed(1)}%</td>
                            <td style={tdS}>{Math.round(fl.reduce((s,t)=>s+(t.cubicVolume||0),0)/fl.length).toLocaleString()}</td>
                            <td style={tdS}>{Math.round(fl.reduce((s,t)=>s+(t.cartons||0),0)/fl.length).toLocaleString()}</td>
                          </tr>
                        );
                      })()}
                      <tr style={{ borderTop: '2px solid #000', fontWeight: 700 }}>
                        <td style={{ ...tdS, textAlign: 'left' }}>TOTAL</td>
                        <td style={tdS}>{filtered.length}</td>
                        <td style={{ ...tdS, fontWeight: 700 }}>{filtered.length > 0 ? (filtered.reduce((s,t) => s + (t.fillPct||0), 0) / filtered.length).toFixed(1) + '%' : '—'}</td>
                        <td style={tdS}>{filtered.length > 0 ? Math.round(filtered.reduce((s,t) => s + (t.cubicVolume||0), 0) / filtered.length).toLocaleString() : '—'}</td>
                        <td style={tdS}>{filtered.length > 0 ? Math.round(filtered.reduce((s,t) => s + (t.cartons||0), 0) / filtered.length).toLocaleString() : '—'}</td>
                      </tr>
                    </tbody>
                  </table>
                  {/* Per-category detail dropdowns */}
                  {CATS.map(cat => {
                    const cl = filtered.filter(t => t.category === cat).sort((a,b) => (b.fillPct||0) - (a.fillPct||0));
                    if (cl.length === 0) return null;
                    const catColor = cat === 'Fluid Direct' ? '#1565c0' : cat === 'Palletized' ? '#e65100' : '#6a1b9a';
                    return (
                      <details key={cat} style={{ marginBottom: 8 }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 700, color: catColor, fontSize: '0.8rem' }}>{cat} — {cl.length} loads (avg {(cl.reduce((s,t)=>s+(t.fillPct||0),0)/cl.length).toFixed(1)}% fill)</summary>
                        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.6rem', marginTop: 4 }}>
                          <thead><tr>
                            <th style={{ padding: '3px 6px', border: '1px solid #ddd', fontSize: '0.55rem', background: catColor, color: '#fff' }}>Date</th>
                            <th style={{ padding: '3px 6px', border: '1px solid #ddd', fontSize: '0.55rem', background: catColor, color: '#fff' }}>Shift</th>
                            <th style={{ padding: '3px 6px', border: '1px solid #ddd', fontSize: '0.55rem', background: catColor, color: '#fff' }}>Door</th>
                            <th style={{ padding: '3px 6px', border: '1px solid #ddd', fontSize: '0.55rem', background: catColor, color: '#fff' }}>VRID</th>
                            <th style={{ padding: '3px 6px', border: '1px solid #ddd', fontSize: '0.55rem', background: catColor, color: '#fff' }}>Time</th>
                            <th style={{ padding: '3px 6px', border: '1px solid #ddd', fontSize: '0.55rem', background: catColor, color: '#fff' }}>Fill %</th>
                            <th style={{ padding: '3px 6px', border: '1px solid #ddd', fontSize: '0.55rem', background: catColor, color: '#fff' }}>Cubic Vol</th>
                            <th style={{ padding: '3px 6px', border: '1px solid #ddd', fontSize: '0.55rem', background: catColor, color: '#fff' }}>Cartons</th>
                          </tr></thead>
                          <tbody>
                            {cl.map((t, i) => (
                              <tr key={i} style={{ background: i%2===0 ? '#fff' : '#f9f9f9' }}>
                                <td style={{ padding: '3px 6px', border: '1px solid #ddd', fontSize: '0.55rem', textAlign: 'center' }}>{t._date}</td>
                                <td style={{ padding: '3px 6px', border: '1px solid #ddd', fontSize: '0.55rem', textAlign: 'center' }}>{t._shift}</td>
                                <td style={{ padding: '3px 6px', border: '1px solid #ddd', fontSize: '0.55rem', textAlign: 'center' }}>{t.door}</td>
                                <td style={{ padding: '3px 6px', border: '1px solid #ddd', fontSize: '0.55rem', textAlign: 'center' }}>{idLink(t.vrid || '—', t.visitId)}</td>
                                <td style={{ padding: '3px 6px', border: '1px solid #ddd', fontSize: '0.55rem', textAlign: 'center' }}>{t.completionTimePST?.split(' ')[1] || '—'}</td>
                                <td style={{ padding: '3px 6px', border: '1px solid #ddd', fontSize: '0.55rem', textAlign: 'center', fontWeight: 600, color: (t.fillPct||0) >= 65 ? '#2e7d32' : (t.fillPct||0) >= 55 ? '#f57f17' : '#c62828' }}>{t.fillPct?.toFixed ? t.fillPct.toFixed(1) : t.fillPct}%</td>
                                <td style={{ padding: '3px 6px', border: '1px solid #ddd', fontSize: '0.55rem', textAlign: 'center' }}>{t.cubicVolume?.toLocaleString ? Math.round(t.cubicVolume).toLocaleString() : t.cubicVolume}</td>
                                <td style={{ padding: '3px 6px', border: '1px solid #ddd', fontSize: '0.55rem', textAlign: 'center' }}>{t.cartons ? Math.round(t.cartons).toLocaleString() : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </details>
                    );
                  })}
                </>);
              })()}
            </>)}
          </div>

          {/* OB Load Cube (Andes v_load_cube) — single combined block */}
          <div className="card">
            <h3 className="section-title" style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} onClick={() => setShowLoadCube(!showLoadCube)}>
              <span>🚛 OB Load Cube</span>
              <span style={{ fontSize: '0.7rem', color: '#666' }}>{showLoadCube ? '▼ Hide' : '▶ Show'}</span>
            </h3>
            {showLoadCube && (<>
              {!loadCube && <p style={{ fontSize: '0.75rem', color: '#888' }}>No load cube data cached yet. Runs from load-cube-sync.sh (Andes v_load_cube, daily ~T-1).</p>}
              {loadCube && (() => {
                const loads = loadCube.loads || [];
                const withFill = loads.filter(l => l.fillPct != null);
                const avgFill = withFill.length ? (withFill.reduce((s, l) => s + l.fillPct, 0) / withFill.length) : 0;
                const avgCube = loads.length ? Math.round(loads.reduce((s, l) => s + (l.totalCube || 0), 0) / loads.length) : 0;
                const totUnits = loads.reduce((s, l) => s + (l.units || 0), 0);
                const totPkgs = loads.reduce((s, l) => s + (l.pkgs || 0), 0);
                const fillColor = (f) => f >= 65 ? '#2e7d32' : f >= 50 ? '#f57f17' : '#c62828';
                const thS = { padding: '4px 8px', border: '1px solid #ddd', fontSize: '0.7rem', background: '#333', color: '#fff', textAlign: 'center' };
                const tdS = { padding: '4px 8px', border: '1px solid #ddd', fontSize: '0.75rem', textAlign: 'center' };
                // VRID search filter for the expandable table
                const q = loadCubeVrid.trim().toUpperCase();
                const shown = q ? loads.filter(l => (l.vrid || '').toUpperCase().includes(q)) : loads;
                const tH = { padding: '3px 6px', border: '1px solid #ddd', fontSize: '0.55rem', background: '#37474f', color: '#fff' };
                const tD = { padding: '3px 6px', border: '1px solid #ddd', fontSize: '0.55rem', textAlign: 'center' };
                return (<>
                  <p style={{ fontSize: '0.62rem', color: '#888', margin: '0 0 6px' }}>
                    {site} origin · last {loadCube.windowBack || 14}d · fill = total cube ÷ trailer capacity
                    {loadCube.generatedAt ? ` · updated ${new Date(loadCube.generatedAt).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
                  </p>
                  <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 8 }}>
                    <thead><tr>
                      <th style={{ ...thS, textAlign: 'left' }}>OB Load Cube</th>
                      <th style={thS}>Loads</th>
                      <th style={thS}>Avg Fill %</th>
                      <th style={thS}>Avg Cubic Vol</th>
                      <th style={thS}>Total Units</th>
                      <th style={thS}>Total Pkgs</th>
                    </tr></thead>
                    <tbody>
                      <tr style={{ fontWeight: 700 }}>
                        <td style={{ ...tdS, textAlign: 'left' }}>All outbound loads</td>
                        <td style={tdS}>{loads.length}</td>
                        <td style={{ ...tdS, color: fillColor(avgFill) }}>{avgFill ? avgFill.toFixed(1) + '%' : '—'}</td>
                        <td style={tdS}>{avgCube.toLocaleString()}</td>
                        <td style={tdS}>{totUnits.toLocaleString()}</td>
                        <td style={tdS}>{totPkgs.toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                  <details style={{ marginBottom: 8 }} open>
                    <summary style={{ cursor: 'pointer', fontWeight: 700, color: '#37474f', fontSize: '0.8rem' }}>
                      Per-load detail — {shown.length}{q ? ` of ${loads.length}` : ''} loads
                    </summary>
                    <div style={{ margin: '6px 0' }}>
                      <input
                        type="text"
                        value={loadCubeVrid}
                        onChange={e => setLoadCubeVrid(e.target.value)}
                        placeholder="Search VRID…"
                        style={{ fontSize: '0.7rem', padding: '3px 8px', width: 220, border: '1px solid #bbb', borderRadius: 4 }}
                      />
                      {q && <button onClick={() => setLoadCubeVrid('')} style={{ fontSize: '0.65rem', marginLeft: 6, padding: '2px 6px', cursor: 'pointer' }}>Clear</button>}
                    </div>
                    <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead><tr>
                          <th style={tH}>Date</th>
                          <th style={tH}>VRID</th>
                          <th style={tH}>Dest</th>
                          <th style={tH}>Arc</th>
                          <th style={tH}>Equip</th>
                          <th style={tH}>Fill %</th>
                          <th style={tH}>Total Cube</th>
                          <th style={tH}>Trailer Cap</th>
                          <th style={tH}>Units</th>
                          <th style={tH}>Pkgs</th>
                          <th style={tH}>Pallets</th>
                        </tr></thead>
                        <tbody>
                          {shown.length === 0 && (
                            <tr><td style={tD} colSpan={11}>No loads match “{loadCubeVrid}”.</td></tr>
                          )}
                          {shown.map((l, i) => (
                            <tr key={l.vrid + '_' + i} style={{ background: i % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                              <td style={tD}>{l.date}</td>
                              <td style={{ ...tD, fontWeight: 600 }}>{l.vrid}</td>
                              <td style={tD}>{l.destination || '—'}</td>
                              <td style={tD}>{l.arcType || '—'}</td>
                              <td style={tD}>{l.equipmentType || '—'}</td>
                              <td style={{ ...tD, fontWeight: 600, color: l.fillPct != null ? fillColor(l.fillPct) : '#999' }}>{l.fillPct != null ? l.fillPct.toFixed(1) + '%' : '—'}</td>
                              <td style={tD}>{l.totalCube != null ? Math.round(l.totalCube).toLocaleString() : '—'}</td>
                              <td style={tD}>{l.equipmentCube != null ? l.equipmentCube.toLocaleString() : '—'}</td>
                              <td style={tD}>{l.units != null ? l.units.toLocaleString() : '—'}</td>
                              <td style={tD}>{l.pkgs != null ? l.pkgs.toLocaleString() : '—'}</td>
                              <td style={tD}>{l.pallets != null ? l.pallets.toLocaleString() : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                </>);
              })()}
            </>)}
          </div>

          {/* Yard Report - side by side path tables */}
          <div className="card">
            <h3 className="section-title" style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} onClick={() => setShowTrailers(!showTrailers)}>
              <span>Yard Report ({(yardData.trailers || []).filter(t => !t.isDefect && t.notes && t.notes.toUpperCase().startsWith('IBVEND')).length} trailers)</span>
              <span style={{ fontSize: '0.7rem', color: '#666' }}>{showTrailers ? '▼ Hide' : '▶ Show'}</span>
            </h3>
            {showTrailers && (
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {PATHS.map(path => {
                  const pathTrailers = (yardData.trailers || [])
                    .filter(t => t.path === path && !t.isDefect && t.notes && t.notes.toUpperCase().startsWith('IBVEND'))
                    .sort((a, b) => b.dwellHours - a.dwellHours);
                  return (
                    <div key={path} style={{ flex: '1 1 200px', minWidth: 220 }}>
                      <h4 style={{ margin: '8px 0 4px', color: PATH_COLORS[path], fontSize: '0.85rem', fontWeight: 700 }}>
                        {path} ({pathTrailers.length})
                      </h4>
                      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.65rem' }}>
                        <thead>
                          <tr>
                            <th style={{ ...cellStyle, padding: '2px 4px', fontSize: '0.6rem', background: PATH_COLORS[path], color: '#fff' }}>Loc</th>
                            <th style={{ ...cellStyle, padding: '2px 4px', fontSize: '0.6rem', background: PATH_COLORS[path], color: '#fff' }}>Vehicle</th>
                            <th style={{ ...cellStyle, padding: '2px 4px', fontSize: '0.6rem', background: PATH_COLORS[path], color: '#fff' }}>Dwell</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pathTrailers.map((t, i) => {
                            const dwellDays = t.dwellHours / 24;
                            const bg = dwellDays >= 5 ? '#ffcdd2' : dwellDays >= 3 ? '#fff3e0' : dwellDays >= 2 ? '#fffde7' : '#fff';
                            return (
                              <tr key={i} style={{ background: bg }}>
                                <td style={{ ...cellStyle, padding: '2px 4px', fontSize: '0.6rem' }}>{t.location}</td>
                                <td style={{ ...cellStyle, padding: '2px 4px', fontSize: '0.6rem' }}>{idLink(t.vehicle, t.visitId)}</td>
                                <td style={{ ...cellStyle, padding: '2px 4px', fontSize: '0.6rem', fontWeight: 600, color: dwellDays >= 5 ? '#c62828' : dwellDays >= 3 ? '#e65100' : '#333' }}>
                                  {dwellDays >= 1 ? `${dwellDays.toFixed(1)}d` : `${Math.round(t.dwellHours)}h`}
                                </td>
                              </tr>
                            );
                          })}
                          {pathTrailers.length === 0 && <tr><td colSpan={3} style={{ ...cellStyle, color: '#999', fontStyle: 'italic' }}>None</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
      <VridForecaster api={api} site={site} />
      <ObTrailersCompleted api={api} site={site} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// VRID Forecaster — per-ARC trailer forecast at the bottom of FALLIN.
//
// Columns: Trailer type (SCAC) | ARC | ARC % (editable) | Expected Volume |
//          Trailers needed next 24 | VRIDs departing next 24 | RTD |
//          In Progress | Scheduled | Loading On VRIDs >24h
//
// Counts (RTD/In Progress/Scheduled/>24h) + SCAC come from VRID_FORECAST_<site>
// (vrid-forecast-sync.sh, every ~5 min). ARC % is a weekly manual list the user
// maintains here (saved to VRID_ARC_PCT_<weekStart>). Expected Volume =
// ARC% x (current + next shift OB cartons from the LVP plan). Trailers needed =
// ceil(ExpectedVolume / 1200). VRIDs departing next 24 = RTD + In Progress +
// Scheduled.
const CARTONS_PER_TRAILER = 1200;
const LVP_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function arcPctColor(pct) {
  // green (low) -> red (high) gradient, matching the sheet.
  if (pct == null) return '#fff';
  if (pct >= 7) return '#f4a0a0';
  if (pct >= 6) return '#f8b4a0';
  if (pct >= 5) return '#fbd0a0';
  if (pct >= 4) return '#fde9a0';
  if (pct >= 3) return '#e6efb0';
  if (pct >= 2) return '#cfe8a8';
  return '#bfe3a0';
}

function VridForecaster({ api, site }) {
  const [forecast, setForecast] = React.useState(null);   // {arcs:{ARC:{scac,rtd,...}}}
  const [arcPct, setArcPct] = React.useState([]);          // [{arc, pct}]
  const [palletsByDest, setPalletsByDest] = React.useState({});  // ARC -> pallet count (RODEO_PALLETS)
  const [lvpCartons, setLvpCartons] = React.useState(0);   // current+next shift OB cartons
  const [weekStart, setWeekStart] = React.useState('');
  const [saved, setSaved] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [emptiesByScac, setEmptiesByScac] = React.useState({});  // {SCAC:{onsite,offsite}} from YMS yard state

  // Current Pacific week start (Sunday) + current/next shift keys.
  React.useEffect(() => {
    const now = new Date();
    const pac = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    const sunday = new Date(pac); sunday.setDate(pac.getDate() - pac.getDay());
    const ws = `${sunday.getFullYear()}-${String(sunday.getMonth() + 1).padStart(2, '0')}-${String(sunday.getDate()).padStart(2, '0')}`;
    setWeekStart(ws);
  }, []);

  const loadForecast = React.useCallback(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=VRID_FORECAST_${site}&shift_type=data`)
      .then(r => r.json()).then(d => setForecast(d && d.arcs ? d : null)).catch(() => setForecast(null));
  }, [api, site]);

  // Empty equipment by SCAC from current YMS yard state (YMS_YARD/current).
  const loadEmpties = React.useCallback(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=YMS_YARD&shift_type=current`)
      .then(r => r.json()).then(d => setEmptiesByScac((d && d.emptiesByScac) || {})).catch(() => setEmptiesByScac({}));
  }, [api, site]);

  // Pallet count per destination (ARC) from RODEO_PALLETS; drives the Pallet
  // Count column + the +1 trailer-per-50-pallets bump.
  React.useEffect(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=RODEO_PALLETS_${site}&shift_type=data`)
      .then(r => r.json())
      .then(d => {
        const m = {};
        if (d && Array.isArray(d.byDest)) d.byDest.forEach(x => { m[(x.destination || '').toUpperCase()] = x.pallets || 0; });
        setPalletsByDest(m);
      })
      .catch(() => setPalletsByDest({}));
  }, [api, site]);

  const loadArcPct = React.useCallback(() => {
    if (!weekStart) return;
    fetch(`${api}/api/fclm/cache?site=${site}&date=VRID_ARC_PCT_${weekStart}&shift_type=data`)
      .then(r => r.json()).then(d => setArcPct(Array.isArray(d?.rows) ? d.rows : [])).catch(() => setArcPct([]));
  }, [api, site, weekStart]);

  // LVP current+next shift OB cartons.
  const loadLvp = React.useCallback(() => {
    if (!weekStart) return;
    fetch(`${api}/api/fclm/cache?site=${site}&date=LVP_${weekStart}&shift_type=plan`)
      .then(r => r.json())
      .then(d => {
        const sd = d?.shiftData || {};
        const pac = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
        const dow = pac.getDay(); const hr = pac.getHours();
        const isDay = hr >= 8 && hr < 19;
        // shift sequence: current shift, then the next one chronologically.
        const seq = [];
        if (isDay) { seq.push([dow, 'day'], [dow, 'night']); }
        else {
          // night now: current night (today if >=19, else yesterday's night), then next day
          if (hr >= 19) { seq.push([dow, 'night'], [(dow + 1) % 7, 'day']); }
          else { seq.push([(dow + 6) % 7, 'night'], [dow, 'day']); }
        }
        let total = 0;
        seq.forEach(([d, s]) => { total += Number(sd[`${LVP_DAYS[d]}_${s}`]?.cartons) || 0; });
        setLvpCartons(total);
      })
      .catch(() => setLvpCartons(0));
  }, [api, site, weekStart]);

  React.useEffect(() => { if (open) { loadForecast(); loadArcPct(); loadLvp(); loadEmpties(); } }, [open, loadForecast, loadArcPct, loadLvp, loadEmpties]);
  React.useEffect(() => {
    if (!open) return;
    const t = setInterval(() => { loadForecast(); loadEmpties(); }, 60000);
    return () => clearInterval(t);
  }, [open, loadForecast, loadEmpties]);

  const persistArcPct = (rows) => {
    setArcPct(rows);
    fetch(`${api}/api/fclm/cache`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site, date: `VRID_ARC_PCT_${weekStart}`, shift_type: 'data', data: { rows, weekStart } }),
    }).then(() => { setSaved(true); setTimeout(() => setSaved(false), 1500); }).catch(() => {});
  };
  const setRow = (i, field, val) => { const r = arcPct.map((x, j) => j === i ? { ...x, [field]: val } : x); persistArcPct(r); };
  const addRow = () => persistArcPct([...arcPct, { arc: '', pct: '', scac: '' }]);
  const delRow = (i) => persistArcPct(arcPct.filter((_, j) => j !== i));

  const cell = { border: '1px solid #999', padding: '3px 8px', fontSize: '0.72rem', textAlign: 'center', whiteSpace: 'nowrap' };
  const hdr = { ...cell, background: '#d7f0f7', fontWeight: 700 };

  // Build rows from the ARC% list, joined to the live forecast counts.
  const arcsData = forecast?.arcs || {};
  const rows = (arcPct || []).map(({ arc, pct, scac }) => {
    const a = (arc || '').trim().toUpperCase();
    const f = arcsData[a] || {};
    const pctNum = parseFloat(pct) || 0;
    const expVol = Math.round((pctNum / 100) * lvpCartons);
    const baseTrailers = Math.ceil(expVol / CARTONS_PER_TRAILER) || 0;
    const palletCount = palletsByDest[a] || 0;
    const palletTrailers = Math.floor(palletCount / 50);  // +1 trailer per 50 pallets
    const trailersNeeded = baseTrailers + palletTrailers;
    const rtd = f.rtd || 0, inProg = f.inProgress || 0, sched = f.scheduled || 0, over24 = f.loadingOver24 || 0;
    // Trailer type: manual value (stored in the weekly ARC% cache) always wins;
    // fall back to the live forecast SCAC when the operator hasn't set one.
    // Because it lives in VRID_ARC_PCT_<weekStart>, it naturally resets each week.
    const scacManual = (scac || '').trim();
    return { arc: a, scac: scacManual || f.scac || '', scacRaw: scac ?? '', pct: pctNum, pctRaw: pct, expVol, palletCount, trailersNeeded,
      departing: rtd + inProg + sched, rtd, inProg, sched, over24 };
  });

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <h3 style={{ fontSize: '0.95rem', margin: 0, cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        {open ? '▾' : '▸'} VRID Forecaster{forecast?.generatedAt && open ? <span style={{ fontWeight: 400, fontSize: '0.68rem', color: '#888' }}>  · updated {new Date(forecast.generatedAt).toLocaleTimeString('en-US', { timeZone: 'America/Los_Angeles', hour: 'numeric', minute: '2-digit' })}</span> : null}
      </h3>
      {open && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: '0.7rem', color: '#555', marginBottom: 6 }}>
            Current + next shift OB cartons (LVP): <strong>{lvpCartons.toLocaleString()}</strong> · {CARTONS_PER_TRAILER}/trailer · week {weekStart}
            {saved && <span style={{ color: '#27ae60', marginLeft: 8 }}>saved ✓</span>}
            <button onClick={addRow} style={{ marginLeft: 10, fontSize: '0.68rem' }}>+ ARC</button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={hdr}>Trailer type</th><th style={hdr}>ARC</th><th style={hdr}>ARC %</th>
                <th style={hdr}>Expected Volume</th><th style={hdr}>Pallet Count</th><th style={hdr}>Trailers needed next 24</th>
                <th style={hdr}>VRID departing next 24</th><th style={hdr}>RTD</th>
                <th style={hdr}>In Progress</th><th style={hdr}>Scheduled</th><th style={hdr}>Loading On VRIDs &gt;24 hours</th>
                <th style={hdr}></th>
              </tr></thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td style={{ ...cell, textAlign: 'left', color: '#888' }} colSpan={12}>No ARCs yet — add this week's ARC + % with “+ ARC”.</td></tr>
                ) : rows.map((r, i) => (
                  <tr key={i}>
                    <td style={{ ...cell, background: '#28c3e8', fontWeight: 600 }}>
                      <input value={r.scacRaw ?? ''} onChange={e => setRow(i, 'scac', e.target.value)} placeholder={arcsData[r.arc]?.scac || '—'} style={{ width: 70, textAlign: 'center', border: 'none', background: 'transparent', fontWeight: 600 }} />
                    </td>
                    <td style={{ ...cell, background: '#28c3e8', textAlign: 'left', fontWeight: 600 }}>
                      <input value={r.arc} onChange={e => setRow(i, 'arc', e.target.value)} style={{ width: 56, border: 'none', background: 'transparent', fontWeight: 600 }} />
                    </td>
                    <td style={{ ...cell, background: arcPctColor(r.pct) }}>
                      <input value={r.pctRaw ?? ''} onChange={e => setRow(i, 'pct', e.target.value)} style={{ width: 42, textAlign: 'right', border: 'none', background: 'transparent' }} />%
                    </td>
                    <td style={{ ...cell, background: '#fde9a0' }}>{r.expVol.toLocaleString()}</td>
                    <td style={cell} title={r.palletCount ? `+${Math.floor(r.palletCount / 50)} trailer(s) from pallets (1 per 50)` : undefined}>{r.palletCount || ''}</td>
                    <td style={cell}>{r.trailersNeeded}</td>
                    <td style={{ ...cell, fontWeight: 700, ...(() => {
                      // M vs L: departing (M) compared to trailersNeeded (L).
                      // M>L yellow (over), M=L green (on target), M<L red (short).
                      if (r.departing > r.trailersNeeded) return { background: '#fde9a0' };
                      if (r.departing === r.trailersNeeded) return { background: '#c6e0b4' };
                      return { background: '#e24a4a', color: '#fff' };
                    })() }}>{r.departing}</td>
                    <td style={{ ...cell, background: r.rtd >= 3 ? '#e24a4a' : '#fff', color: r.rtd >= 3 ? '#fff' : '#000', fontWeight: r.rtd >= 3 ? 700 : 400 }}>{r.rtd}</td>
                    <td style={cell}>{r.inProg}</td>
                    <td style={cell}>{r.sched}</td>
                    <td style={{ ...cell, background: r.over24 > 0 ? '#e24a4a' : '#fff', color: r.over24 > 0 ? '#fff' : '#000', fontWeight: r.over24 > 0 ? 700 : 400 }}>{r.over24}</td>
                    <td style={{ border: 'none', padding: '0 4px' }}><button onClick={() => delRow(i)} title="remove" style={{ fontSize: '0.6rem', color: '#c0392b' }}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Equipment Available — empty equipment (by SCAC/trailer type) in the
              yard, for the trailer types present in the forecaster above. Onsite/
              Offsite from YMS yard state; Needed = sum of "trailers needed next
              24" across forecaster rows of that SCAC; Delta = (on+off) − needed. */}
          {(() => {
            // SCACs present in the forecaster (manual-wins value), deduped.
            const scacsInForecaster = [...new Set(rows.map(r => (r.scac || '').trim().toUpperCase()).filter(Boolean))];
            if (scacsInForecaster.length === 0) return null;
            // Needed per SCAC = sum of trailersNeeded across forecaster rows sharing it.
            const neededByScac = {};
            rows.forEach(r => {
              const s = (r.scac || '').trim().toUpperCase();
              if (!s) return;
              neededByScac[s] = (neededByScac[s] || 0) + (r.trailersNeeded || 0);
            });
            return (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontWeight: 700, fontSize: '0.8rem', marginBottom: 4 }}>Equipment Available (empties in yard by trailer type)</div>
                <table style={{ borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={hdr}>Trailer type</th>
                    <th style={hdr}>Onsite</th>
                    <th style={hdr}>Offsite</th>
                    <th style={hdr}>Needed</th>
                    <th style={hdr}>Incoming</th>
                    <th style={hdr}>Delta</th>
                  </tr></thead>
                  <tbody>
                    {scacsInForecaster.map(s => {
                      const e = emptiesByScac[s] || { onsite: 0, offsite: 0 };
                      const onsite = e.onsite || 0;
                      const offsite = e.offsite || 0;
                      const needed = neededByScac[s] || 0;
                      const incoming = 0; // TODO: wire from FMC inbound empties by SCAC
                      const delta = (onsite + offsite + incoming) - needed;
                      // Delta highlight: short = red, surplus = amber, exact = green.
                      const deltaBg = delta < 0 ? '#e24a4a' : delta > 0 ? '#fde9a0' : '#c6e0b4';
                      const deltaFg = delta < 0 ? '#fff' : '#000';
                      return (
                        <tr key={s}>
                          <td style={{ ...cell, background: '#28c3e8', fontWeight: 600 }}>{s}</td>
                          <td style={cell}>{onsite}</td>
                          <td style={cell}>{offsite}</td>
                          <td style={cell}>{needed}</td>
                          <td style={{ ...cell, color: '#888' }}>{incoming}</td>
                          <td style={{ ...cell, background: deltaBg, color: deltaFg, fontWeight: 700 }}>{delta > 0 ? `+${delta}` : delta}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ fontSize: '0.62rem', color: '#888', marginTop: 4 }}>
                  Empties = yard equipment with no assigned VRID. Incoming (FMC) coming soon. Delta = (Onsite + Offsite + Incoming) − Needed.
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// OB Trailers Completed — Q1-Q4 breakdown of OB_DOCK_COMPLETED trailers, split
// Fluid Direct / Fluid Offline / Palletized, drillable by date + shift. Reads
// the OB_FILL_<date> cache (shift_type=day|night) built by fmc-fill-sync.sh;
// each trailer carries category + completionTimePST, which drives the quarter.
// Quarter boundaries match the shift break windows (same as EOL quarterly):
//   Day:   Q1 <10:15 · Q2 10:15-13:15 · Q3 13:15-16:15 · Q4 >=16:15
//   Night: Q1 <21:15 · Q2 21:15-00:15 · Q3 00:15-03:15 · Q4 >=03:15 (wraps midnight)
function ObTrailersCompleted({ api, site }) {
  const [open, setOpen] = React.useState(false);
  const [date, setDate] = React.useState('');
  const [shift, setShift] = React.useState('day');
  const [data, setData] = React.useState(null);

  // Default to the current Pacific date + shift.
  React.useEffect(() => {
    const pac = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    const h = pac.getHours();
    // Before 07:00 PT we're still in the prior day's night shift.
    let d = new Date(pac);
    if (h < 7) d.setDate(d.getDate() - 1);
    const ds = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setDate(ds);
    setShift(h >= 7 && h < 19 ? 'day' : 'night');
  }, []);

  React.useEffect(() => {
    if (!date) return;
    let alive = true;
    fetch(`${api}/api/fclm/cache?site=${site}&date=OB_FILL_${date}&shift_type=${shift}`)
      .then(r => r.json())
      .then(d => { if (alive) setData(d && Array.isArray(d.trailers) ? d : { trailers: [] }); })
      .catch(() => { if (alive) setData({ trailers: [] }); });
    return () => { alive = false; };
  }, [api, site, date, shift]);

  // Map a completionTimePST ('YYYY-MM-DD HH:MM') to a shift quarter 1-4.
  const quarterOf = (completionPST) => {
    if (!completionPST || completionPST.indexOf(' ') < 0) return null;
    const hm = completionPST.split(' ')[1];
    const [hh, mm] = hm.split(':').map(Number);
    if (isNaN(hh)) return null;
    let mins = hh * 60 + mm;
    if (shift === 'day') {
      if (mins < 10 * 60 + 15) return 1;         // <10:15
      if (mins < 13 * 60 + 15) return 2;         // 10:15-13:15
      if (mins < 16 * 60 + 15) return 3;         // 13:15-16:15
      return 4;                                   // >=16:15
    }
    // Night wraps midnight: treat 00:00-07:00 as +24h so comparisons are linear.
    if (mins < 7 * 60) mins += 24 * 60;
    if (mins < 21 * 60 + 15) return 1;           // <21:15
    if (mins < 24 * 60 + 15) return 2;           // 21:15-00:15
    if (mins < 27 * 60 + 15) return 3;           // 00:15-03:15
    return 4;                                     // >=03:15
  };

  const CATS = [['Fluid Direct', 'Fluid Direct'], ['Fluid Offline', 'Fluid Offline'], ['Palletized', 'Palletized']];
  // grid[q][cat] = count
  const grid = React.useMemo(() => {
    const g = { 1: {}, 2: {}, 3: {}, 4: {} };
    [1, 2, 3, 4].forEach(q => CATS.forEach(([c]) => { g[q][c] = 0; }));
    (data?.trailers || []).forEach(t => {
      const q = quarterOf(t.completionTimePST);
      const c = t.category;
      if (q && g[q] && c in g[q]) g[q][c] += 1;
    });
    return g;
  }, [data, shift]);

  const colTotals = React.useMemo(() => {
    const tot = {}; CATS.forEach(([c]) => { tot[c] = [1, 2, 3, 4].reduce((s, q) => s + grid[q][c], 0); });
    return tot;
  }, [grid]);
  const grand = CATS.reduce((s, [c]) => s + colTotals[c], 0);

  const hdr = { border: '1px solid #ccc', padding: '3px 10px', fontSize: '0.7rem', background: '#232f3e', color: '#fff', textAlign: 'center', fontWeight: 600 };
  const cell = { border: '1px solid #ccc', padding: '3px 10px', fontSize: '0.72rem', textAlign: 'center' };
  const rowHdr = { ...cell, fontWeight: 700, background: '#f1f8e9', textAlign: 'left' };

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <h3 style={{ fontSize: '0.95rem', margin: 0, cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        {open ? '▾' : '▸'} OB Trailers Completed (by quarter)
      </h3>
      {open && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
            <label style={{ fontSize: '0.72rem' }}>Date: <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ fontSize: '0.72rem' }} /></label>
            <label style={{ fontSize: '0.72rem' }}>Shift:{' '}
              <select value={shift} onChange={e => setShift(e.target.value)} style={{ fontSize: '0.72rem' }}>
                <option value="day">Day</option><option value="night">Night</option>
              </select>
            </label>
            <span style={{ fontSize: '0.7rem', color: '#555' }}>{grand} trailer{grand === 1 ? '' : 's'} completed · OB_DOCK_COMPLETED time</span>
          </div>
          <table style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...hdr, textAlign: 'left' }}>Quarter</th>
                {CATS.map(([c, lbl]) => <th key={c} style={hdr}>{lbl}</th>)}
                <th style={hdr}>Total</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4].map(q => {
                const rowTot = CATS.reduce((s, [c]) => s + grid[q][c], 0);
                return (
                  <tr key={q}>
                    <td style={rowHdr}>Q{q}</td>
                    {CATS.map(([c]) => <td key={c} style={cell}>{grid[q][c] || ''}</td>)}
                    <td style={{ ...cell, fontWeight: 600 }}>{rowTot || ''}</td>
                  </tr>
                );
              })}
              <tr>
                <td style={rowHdr}>Total</td>
                {CATS.map(([c]) => <td key={c} style={{ ...cell, fontWeight: 700, background: '#eef2f7' }}>{colTotals[c] || ''}</td>)}
                <td style={{ ...cell, fontWeight: 700, background: '#eef2f7' }}>{grand || ''}</td>
              </tr>
            </tbody>
          </table>
          {grand === 0 && <p style={{ fontSize: '0.7rem', color: '#888', marginTop: 6 }}>No completed OB trailers cached for {date || 'this date'} ({shift}).</p>}
        </div>
      )}
    </div>
  );
}
